/**
 * FreeWheel custom functions.
 */
import has from 'lodash/has';

const GamKeysToFreeWheelKeys = {
  us_privacy: '_fw_us_privacy',
  playerwidth: '_fw_player_width',
  playerheight: '_fw_player_height',
  ppid: ['_fw_vcid2', 'ppid'],
  content_genre: '_fw_content_genre',
  content_cat: 'content_cat',
  content_channel: 'content_channel',
  vpif: 'vpif',
  hlmeta: 'hlmeta',
  refid: 'refid',
  pagetype: 'pagetype',
  pid: 'pid',
  upid: 'upid',
  pers_cid: 'pers_cid',
  page_key: 'page_key',
  nlpsent: 'nlpsent',
  _fw_content_language: '_fw_content_language',
};

const getDevice = () => {
  const width = (window.innerWidth || document.documentElement.clientWidth);
  let device = 'Mobile';
  if (480 <= width) {
    device = 'Tablet';
  }
  if (1024 <= width) {
    device = 'Desktop';
  }
  return device;
};

const getOS = () => {
  const { userAgent } = window.navigator;
  const platform = window.navigator?.userAgentData?.platform
    || window.navigator?.platform;
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  let os = '';

  if (- 1 !== iosPlatforms.indexOf(platform)
  || platform?.toLowerCase()?.includes('mac')) {
    os = 'ios';
  } else if (/Android/.test(userAgent)) {
    os = 'android';
  }

  return os;
};

/* A dynamic value created by the user that turns
  *the page/video player into a test page.
*/
export const addPageKey = () => {
  const urlParamsEncoded = encodeURIComponent(window.location.search); // phpcs:ignore WordPressVIPMinimum.JS.Window.location
  const urlParamsFormatted = urlParamsEncoded
    .substring(3)
    .replace('%26', '&')
    .replace('%3D', '=');
  const urlParams = new URLSearchParams(urlParamsFormatted);
  if (urlParams.has('page_key')) {
    return urlParams.get('page_key');
  }
  return null;
};

// Type of page the video player is on
// eslint-disable-next-line camelcase
export const addPageType = () => (window.amScripts?.page_attributes?.page_type || window.NXSTdata?.content?.pageType); // eslint-disable-line max-len

export const isFreewheelAdURL = (url = '') => url?.includes('fwmrm');

const updateGlobalParams = (globalSection, params = {}) => {
  const vprn = Math.floor(Math.random() * 90000) + 10000;
  if (! window.pvrn) {
    window.pvrn = Math.floor(Math.random() * 90000) + 10000;
  }

  const globalParams = new URLSearchParams(globalSection);
  let csidVal = globalParams.get('csid');
  if (csidVal && ! csidVal.includes('vod')) { // Workaround to prevent multiple executions
    const deviceType = getDevice();
    const device = 'Desktop' === deviceType ? 'web' : 'mobile';
    let platform = 'desktop';
    if ('Desktop' !== deviceType) {
      if (getOS()) {
        platform = getOS() + ('Mobile' === deviceType ? 'phone' : 'tablet');
      } else {
        platform = 'unknown';
      }
    }
    csidVal += `${'_vod_'}${device}_${platform}`;
    globalParams.set('csid', csidVal);
  }

  globalParams.set('vprn', vprn);
  globalParams.set('pvrn', window.pvrn);

  if (params.videoId) {
    globalParams.set('caid', params.videoId);
  }

  if (params.vdur) {
    globalParams.set('vdur', params.vdur);
  }

  return globalParams.toString();
};

const updateKeyValues = (keySection, params) => {
  const keyValues = new URLSearchParams(keySection);

  const pageKey = addPageKey();
  const pageType = addPageType();

  if (pageKey) {
    keyValues.set('page_key', pageKey);
  }

  if (pageType) {
    keyValues.set('pagetype', pageType);
  }

  Object.keys(params).forEach((key) => {
    if (has(GamKeysToFreeWheelKeys, key)) {
      if (Array.isArray(GamKeysToFreeWheelKeys[key])) {
        GamKeysToFreeWheelKeys[key].forEach((fwKey) => {
          keyValues.set(fwKey, params[key]);
        });
        return;
      }

      keyValues.set(GamKeysToFreeWheelKeys[key], params[key]);
    }
  });
  /* eslint-disable no-underscore-dangle */
  if (params?.permutive) {
    keyValues.delete('permutive');
    const googleCohorts = decodeURIComponent(params.permutive).split(',');
    const freewheelCohorts = JSON.parse(localStorage?._pfws || '[]');
    const permutiveCohorts = new Set([...googleCohorts, ...freewheelCohorts]);
    permutiveCohorts.forEach((cohort) => {
      keyValues.append('permutive', cohort); // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.append
    });
  }
  /* eslint-enable no-underscore-dangle */
  // eslint-disable-next-line camelcase
  if (params?.d_code) {
    keyValues.delete('d_code');
    // eslint-disable-next-line camelcase
    const dCodeValues = params?.d_code.split(',');
    dCodeValues.forEach((code) => {
      keyValues.append('d_code', code); // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.append
    });
  }

  keyValues.set('vpif', 'visible' === document.visibilityState);

  const { origin, pathname } = window.location; // phpcs:ignore WordPressVIPMinimum.JS.Window.VarAssignment
  keyValues.set('_fw_site_page', `${origin}${pathname}`);

  // Fixed values
  keyValues.set('_fw_content_rating', 'unrated');
  keyValues.set('ad_manager_nonce', '[GOOGLE_INSTREAM_VIDEO_NONCE]');
  if (window && window.NXSTdata?.privacy?.usPrivacyString) {
    keyValues.set('_fw_us_privacy', window.NXSTdata?.privacy?.usPrivacyString);
  }

  if (window && window.NXSTdata.content.nlpSentiment) {
    const nlpScore = window.NXSTdata.content.nlpSentiment.score;
    let nlpAdScore = '0';
    if (0 !== nlpScore) {
      nlpAdScore = 0 < nlpScore ? 'positive' : 'negative';
    }
    keyValues.set('nlpsent', nlpAdScore);
  }

  return keyValues.toString();
};

const updateSlotParams = (slotSection) => {
  const slotParams = new URLSearchParams(slotSection);
  return slotParams.toString();
};

export const updateFreewheelUrl = (adUrl, params = {}) => {
  if (! adUrl) return adUrl;

  // Query is not second parameter of the spreaded array, bc when there is a query param in the
  // Url(e.g. page_url?page_key=skipbutton) it breaks the spread logic since there will be additional "?" in the _fw_site_page value
  let [url] = adUrl.split('?');
  let query = adUrl.split('?')?.slice(1)?.join('?');
  url = url.toString();

  query = (query || '').split(';');

  let globalParams = query[0] || '';
  let keyValues = query[1] || '';
  const slotParams = query.slice(2);

  globalParams = updateGlobalParams(globalParams, params);
  keyValues = updateKeyValues(keyValues, params);

  Object.keys(slotParams).forEach((i) => {
    slotParams[i] = updateSlotParams(slotParams[i], params);
  });

  query = [globalParams, keyValues, ...slotParams];
  return `${url}?${decodeURIComponent(query.join(';'))}`;
};
